import { createContext, useEffect, useReducer, useContext } from "react";
import axios from "axios";
import Loading from "../Components/MatxLoading";
import { jwtDecode } from "jwt-decode";
import {ApiBaseUrl}  from "../Services/config";
import { useNavigate } from "react-router-dom";

const initialState = {
  user: null,
  lookups: null,
  isInitialised: false,
  isAuthenticated: false,
};

const isValidToken = (accessToken) => {
  if (!accessToken) return false;

  try {
    const currentTime = Date.now() / 1000;
    const expiresOn = new Date(accessToken.ExpiresOn).getTime() / 1000;
    console.log('Current time:', currentTime, 'Expires on:', expiresOn);
    return expiresOn > currentTime;
  } catch (error) {
    console.error('Error validating token:', error);
    return false;
  }
};


export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

const setSession = (accessToken) => {
  if (accessToken) {
    localStorage.setItem("jwt_access_token", JSON.stringify(accessToken));
  } else {
    localStorage.removeItem("jwt_access_token");
  }
};

const reducer = (state, action) => {
  switch (action.type) {
    case "INITIALISE": {
      const { isAuthenticated, user, lookups } = action.payload;
      return {
        ...state,
        isAuthenticated,
        isInitialised: true,
        user,
        lookups,
      };
    }
    case "LOGIN": {
      return {
        ...state,
        isAuthenticated: true,
        user: action.payload.user,
        lookups: action.payload.lookups,
      };
    }
    case "LOGOUT": {
      return {
        ...state,
        user: null,
        lookups: null,
        isAuthenticated: false,
      };
    }
    default: {
      return { ...state };
    }
  }
};

const AuthContext = createContext({
  ...initialState,
  method: "JWT",
  login: () => {},
  logout: () => {},
  register: () => {},
});

export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const navigate = useNavigate();

  const setUserLookUps = () => {
    try {
      const tokenObj = localStorage.getItem("jwt_access_token") ? JSON.parse(localStorage.getItem("jwt_access_token")): null;
      const token = tokenObj?.Token;
      
      if (token && isValidToken(tokenObj)) {
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        const lookups = localStorage.getItem("lookups") ? JSON.parse(localStorage.getItem("lookups")) : null;
        dispatch({
          type: "INITIALISE",
          payload: {
            isAuthenticated: true,
            user: tokenObj,
            lookups,
          },
        });
      } else {
        // Clear invalid or expired token
        localStorage.removeItem("jwt_access_token");
        delete axios.defaults.headers.common['Authorization'];
        dispatch({
          type: "INITIALISE",
          payload: {
            isAuthenticated: false,
            user: null,
            lookups: null,
          },
        });
      }
    } catch (error) {
      console.error('Error in setUserLookUps:', error);
      localStorage.removeItem("jwt_access_token");
      delete axios.defaults.headers.common['Authorization'];
      dispatch({
        type: "INITIALISE",
        payload: {
          isAuthenticated: false,
          user: null,
          lookups: null,
        },
      });
    }
  };

  useEffect(() => {
    setUserLookUps();
  }, []);

  const login = async (email, password) => {
    try {
      const response = await axios.post(ApiBaseUrl+"/api/auth/login", {
        email,
        password,
      });
      console.log('Login response:', response.data);
      
      setSession(response.data);
      
      // Set axios default header for future requests
      if (response.data.Token) {
        axios.defaults.headers.common['Authorization'] = `Bearer ${response.data.Token}`;
      }
      
      dispatch({
        type: "LOGIN",
        payload: {
         user: response.data,
        },
      });
      
      if (response.data.Role === "Sales") {
        navigate("/dashboard");
      } else {
        navigate("/");
      }
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  };

  const register = async (values) => {
    try {
      const response = await axios.post(ApiBaseUrl+"/api/auth/register", values);
      const { token, lookups } = response.data;
      setSession(token);
      const user = jwtDecode(token);
      dispatch({
        type: "LOGIN",
        payload: {
          user,
          lookups,
        },
      });
      navigate("/");
    } catch (error) {
      console.error(error);
      throw error;
    }
  };

  const logout = () => {
    setSession(null);
    delete axios.defaults.headers.common['Authorization'];
    dispatch({ type: "LOGOUT" });
    navigate("/");
  };

  if (!state.isInitialised) {
    return <Loading loading={true} />;
  }

  return (
    <AuthContext.Provider
      value={{
        ...state,
        method: "JWT",
        login,
        logout,
        register,
        setUserLookUps,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
export default AuthContext;
